/**
 * Récapitulatif des marqueurs : un marqueur, quatre verdicts.
 *
 * Partagé par la vue d'ensemble et la section Marqueurs. Les deux pages
 * affichent le MÊME tableau — l'une en lecture, l'autre avec les boutons de
 * modification — et deux calculs jumeaux auraient divergé à la première
 * correction du mode `growth`.
 *
 * ── Pourquoi quatre métriques et pas une ────────────────────────────────────
 * Une mise à jour peut faire grimper l'usage sans rien changer aux
 * abonnements, et l'inverse arrive aussi. Deux flux (actions, actifs) et deux
 * cumuls (comptes, abonnés) : une seule métrique laisserait conclure trop vite.
 *
 * ── Les marqueurs hors période sont listés quand même ───────────────────────
 * Leurs quatre colonnes valent « — » : les fenêtres de comparaison sont bornées
 * par la série tracée, et un marqueur dont la période ne couvre pas les
 * alentours n'a rien à comparer. Ils restent affichés — avec la mention
 * `outOfPeriod` — parce que la liste sert aussi de mémoire : masquer un
 * évènement parce qu'il est vieux ferait croire qu'il n'existe pas.
 */

import { markerShownIn } from '@shared/types/stats';
import { useMemo } from 'react';

import {
  computeImpact,
  formatImpactDelta,
  formatImpactWindows,
  impactTone,
  seriesFrom,
  verdictLabel,
  type ImpactMode,
  type ImpactTone,
  type ImpactUnit,
  type MarkerImpact,
  type SeriesPoint,
} from '../lib/markers';
import { withMissingDays } from '../lib/series';
import type { Marker, PeriodStats } from '../types';
import { useMarkers } from './useMarkers';

/** Série de snapshots densifiée, telle que `withMissingDays` la produit. */
type AccountsSeries = ReturnType<typeof withMissingDays>;

export interface ImpactMetric {
  key: string;
  header: string;
  /** `level` pour un flux, `growth` pour un cumul. Cf. `lib/markers.ts`. */
  mode: ImpactMode;
  unit: ImpactUnit;
  higherIsBetter: boolean;
  series: (stats: PeriodStats, accounts: AccountsSeries) => SeriesPoint[];
}

/**
 * Les quatre métriques, dans l'ordre d'affichage : de l'usage vers l'argent.
 *
 * `mode: 'growth'` sur les deux cumuls — comparer les moyennes du nombre de
 * comptes donnerait « en hausse » même si les inscriptions s'étaient arrêtées
 * net le jour du marqueur.
 */
export const IMPACT_METRICS: readonly ImpactMetric[] = [
  {
    key: 'actions',
    header: 'Actions / j',
    mode: 'level',
    unit: 'count',
    higherIsBetter: true,
    series: (stats) => seriesFrom(stats.dailyActivity, (row) => row.actions),
  },
  {
    key: 'activeUsers',
    header: 'Actifs / j',
    mode: 'level',
    unit: 'count',
    higherIsBetter: true,
    series: (stats) => seriesFrom(stats.dailyActivity, (row) => row.activeUsers),
  },
  {
    key: 'accounts',
    header: 'Comptes / j',
    mode: 'growth',
    unit: 'count',
    higherIsBetter: true,
    series: (_stats, accounts) => seriesFrom(accounts, (row) => row.totalAccounts),
  },
  {
    key: 'subscribers',
    header: 'Abonnés / j',
    mode: 'growth',
    unit: 'count',
    higherIsBetter: true,
    series: (_stats, accounts) => seriesFrom(accounts, (row) => row.totalSubscribers),
  },
];

export interface MarkerImpactCell {
  metric: ImpactMetric;
  impact: MarkerImpact;
  tone: ImpactTone;
  /** Écart déjà mis en forme, ou « — » faute de recul. */
  delta: string;
  /** Détail des deux fenêtres, pour le `title` de la cellule. */
  detail: string;
  verdict: string;
}

export interface MarkerRecapRow {
  marker: Marker;
  /** Vide tant que les statistiques n'ont pas répondu. */
  impacts: MarkerImpactCell[];
  /** La période affichée ne couvre pas la date du marqueur. */
  outOfPeriod: boolean;
}

export interface MarkerRecap {
  rows: MarkerRecapRow[];
  /** Marqueurs en base, toutes périodes confondues. */
  total: number;
  isPending: boolean;
  error: Error | null;
}

/**
 * Une ligne par marqueur, du plus récent au plus ancien.
 *
 * Ordre décroissant et non chronologique : sur les deux pages, ce qu'on vient
 * de changer est ce qu'on vient vérifier. L'historique complet se lit en
 * descendant.
 */
export function useMarkerRecap(
  stats: PeriodStats | undefined,
  from: string,
  to: string,
  /**
   * `analytics` : seulement les marqueurs affichés sur les analyses (résumé du
   * tableau de bord). Absent : tous, pour la page Marqueurs.
   */
  family?: 'analytics',
): MarkerRecap {
  const { data: markers, isPending, error } = useMarkers();

  // Les deux series de snapshots doivent etre densifiees avant tout calcul de
  // pente : sans les jours nuls, un increment enjamberait un trou et le gain
  // de trois jours serait attribue a un seul.
  const accounts = useMemo(
    () => withMissingDays(stats?.accountsTimeline ?? [], from, to),
    [stats?.accountsTimeline, from, to],
  );

  const rows = useMemo<MarkerRecapRow[]>(() => {
    const series = stats
      ? IMPACT_METRICS.map((metric) => ({ metric, points: metric.series(stats, accounts) }))
      : [];

    return [...(markers ?? [])]
      .filter((marker) => !family || markerShownIn(marker, family))
      // Comparaison lexicographique : valide sur du YYYY-MM-DD zero-padde.
      .sort((a, b) => (a.date === b.date ? b.id - a.id : b.date.localeCompare(a.date)))
      .map((marker) => ({
        marker,
        outOfPeriod: marker.date < from || marker.date > to,
        // Les colonnes d'impact mesurent l'USAGE de l'app : un marqueur réservé
        // aux ventes n'y a pas de verdict (« — »).
        impacts: (markerShownIn(marker, 'analytics') ? series : []).map(({ metric, points }) => {
          const impact = computeImpact(points, marker.date, metric.mode);
          return {
            metric,
            impact,
            tone: impactTone(impact.verdict, metric.higherIsBetter),
            delta: formatImpactDelta(impact, metric.unit),
            detail: formatImpactWindows(impact, metric.unit),
            verdict: verdictLabel(impact.verdict),
          };
        }),
      }));
  }, [markers, stats, accounts, from, to, family]);

  return { rows, total: markers?.length ?? 0, isPending, error };
}
